import React, {
  forwardRef,
  useCallback
} from "react";
import {makeStyles} from "@mui/styles";
import {
  Button as MuiButton,
  FormControlLabel,
  Radio,
  TextField,
  Typography,
  DialogContent as MuiDialogContent,
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
} from '@mui/material';

import clsx from 'clsx';

export const colorPrimary = '#009340';
export const colorSecond = '#0070ff';
export const colorLabel = 'rgba(104, 104, 104, 0.6)';
// export const colorText = '#373737';
export const colorText = '#1a173b';

export const colorOrdered = '#00d0ff';
export const colorInProgress = '#f57c00';
export const colorCompleted = '#d41f16';
export const colorInFreight = '#d41f16';
export const colorFinished = '#009340';
export const colorSettled = '#0077ff';
export const colorCanceledOrder = '#bcbcbc';
export const colorCanceledAllocation = '#bcbcbc';
export const colorCanceledFreight = '#bcbcbc';

export const statusTotal = 99;

export const borderStyle = 'solid';
export const borderColor = '#ebedf8';
export const borderWidth = 1;
export const border = 'solid 1px #d0d8e6'

export const fontFamily = 'NotoSansKR';


const useStyles = makeStyles(() => ({
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1a173b',
  },
  subTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    // letterSpacing: 0.11,
    color: '#1a173b',
  },
  label: {
    display: 'flex',
    color: colorLabel,
    fontSize: 11,
  },
  body: {
    fontSize: 13,
    fontWeight: 500,
    color: colorText,
  },
  formControlLabel: {
    fontSize: 11,
    letterSpacing: -0.34,
    color: colorLabel,
    marginTop: 11,
  },
  formControlLabelRadio: {
    marginRight: 22,
  },
  divider: {
    border: 'solid 1px #e9edf3',
  },
  button: {
    display: 'inline-block',
    minWidth: 55,
    minHeight: 28,
    textAlign: 'center',
    fontSize: 11,
  },
  buttonAddressSearch: {
    marginTop: 20, // 주소 검색 버튼을 기본주소 밑 보더에 맞추기
  },
  buttonSearch: {
    fontWeight: 500,
  },
  required: {
    paddingLeft: 2,
    color: colorPrimary,
  },
  customDateInput: {
    width: 100,
  },

  dialogTitle: {
    paddingTop: 27,
    paddingBottom: 15,
    textAlign: 'center',
  },
  dialogContent: {
    paddingTop: 0,
    paddingLeft: 24,
    paddingRight: 24,
    paddingBottom: 8,
    overflow: 'hidden',
  },
  pageTitle: {
    fontSize: 26,
    fontWeight: 'bold',
    color: '#1a173b',
  },
}));

export const useCommonStyles = makeStyles(() => ({
  itemRow: {
    marginTop: 15,
  },
}))

export const itemRowSpacing = 2;

export function Title(props: any) {
  const classes = useStyles();
  const { className } = props;

  return (
    <Typography variant={'h1'} className={clsx(classes.title, className)}>{props.children}</Typography>
  )
}

export const PageTitle = (props: any) => {
  const classes = useStyles();
  const {className} = props;

  return (
    <Typography variant={'h3'} className={clsx(classes.pageTitle, className)}>{props.children}</Typography>
  )
}

export function SubTitle(props: any) {
  const classes = useStyles();
  const { className } = props;

  return (
    <Typography variant={'h2'} className={clsx(classes.subTitle, className)}>{props.children}</Typography>
  )
}

export function Label(props: any) {
  const classes = useStyles();
  const { required } = props

  return (
    <Typography variant={'subtitle1'} className={classes.label}>{props.children}{required && <span className={classes.required}>*</span>}</Typography>
  )
}

export function ControlLabel(props: any) {
  const classes = useStyles();
  const { name, className } = props

  return (
      <span className={clsx(classes.formControlLabel, className)}>{name}</span>
  )
}

export function FormControlLabelRadio(props: any) {
  const classes = useStyles();
  const { value, label } = props;

  return (
    <FormControlLabel
      value={value}  // The DOM API casts this to a string.
      control={<Radio color={"default"}/>}
      label={label}
      className={classes.formControlLabelRadio}
    />
  )
}

export function Body(props: any) {
  const classes = useStyles();
  const { className } = props;

  return (
    <Typography variant={'body1'} className={clsx(classes.body, className)}>{props.children}</Typography>
  )
}

export function Divider() {
  const classes = useStyles();

  return (
    <hr className={classes.divider}/>
  )
}

export const Button = (props: any) => {
  const classes = useStyles();
  const { type, color, onClick, className, disabled, size, sx } = props;

  return (
    <MuiButton
      variant={'contained'}
      size={size ? size : "small"}
      type={type ? type : 'button'}
      color={color ? color : 'primary'}
      className={clsx(classes.button, className)}
      disabled={disabled ? disabled : false}
      onClick={onClick}
      sx={sx ? sx : ""}
    >{props.children}</MuiButton>
  )
}

export const ButtonAddressSearch = (props: any) => {
  const classes = useStyles();
  const { onClick, className } = props;

  return (
    <Button
      className={clsx(classes.buttonAddressSearch, className)}
      onClick={onClick}
      size={"medium"}
    >
      {props.children ? props.children : '주소검색'}
    </Button>
  )
}

export const ButtonSearch = (props: any) => {
  const classes = useStyles();

  return (
    <MuiButton
      type="submit"
      variant="contained"
      color="primary"
      size="small"
      className={clsx(classes.button, classes.buttonSearch)}
    >
      {props.children}
    </MuiButton>
  )
}

export const CustomDateInput = () => {
  const classes = useStyles();

  return forwardRef(
  ({ value, onClick }:any, ref) => (
      <TextField
        required
        value={value}
        variant={"standard"}
        onClick={onClick}
        InputLabelProps={{
          shrink: false,
        }}
        className={classes.customDateInput}
      />
    ),
  );
}

export const DialogTitle = (props: any) => {
  const classes = useStyles();

  return (
    <SubTitle className={classes.dialogTitle}>{props.children}</SubTitle>
  )
}

export const DialogContent = (props: any) => {
  const classes = useStyles();
  const { className } = props;

  return (
    <MuiDialogContent className={clsx(classes.dialogContent, className)}>{props.children}</MuiDialogContent>
  )
}

export const EnhancedTableHead = (props: any) => {
  const { orderType, orderBy, onRequestSort, headCells } = props;

  const createSortHandler = useCallback((property: any) => (event: any) => {
    onRequestSort(event, property);
  }, [onRequestSort]);

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell: any) => (
          <TableCell
            key={headCell.id}
            sortDirection={orderBy === headCell.id ? orderType : false}
          >
            {headCell.sortable ?
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? orderType : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                <Label>{headCell.label}</Label>
              </TableSortLabel> : <Label>{headCell.label}</Label>
            }
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}
